<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import { CheckCircleOutlined, CloudUploadOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { useCriarCompromissoAvulso } from '~/composables/useCriarCompromissoAvulso';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  compromissoCodigos?: string[];
  compromisso?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const usuariosQuery = gql`
  query usuariosSelect(
    $where: UsuarioModelFilterInput
    $order: [UsuarioModelSortInput!]
  ) {
    data: usuariosAll2(where: $where, order: $order) {
      value: codigo
      label: nome
    }
  }
`;

const formRef = ref<any>();
const uploaderRef = ref<any>();

const model = reactive({
  responsavel: null as any,
  descricao: '',
  dataPrazo: null as any,
  arquivos: [] as any[],
});

const { status, result, errorMessage, submit, stopPolling, setLoading } = useCriarCompromissoAvulso();

const mostrarHistoricoSolicitacoes = ref('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];

function disabledDate(current: any) {
  return (
    current.day() === 0 ||
    current.day() === 6 ||
    current <= dayjs().subtract(1, 'day').endOf('day')
  );
}

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

const isLoading = computed(() => status.value === 'loading');
const isSuccess = computed(() => status.value === 'success');
const isPartial = computed(() => status.value === 'partial');
const showForm = computed(() => !isSuccess.value && !isPartial.value);

const isLote = computed(() => {
  const totalPastas = props.pastaCodigos?.length ?? 0;
  const totalCompromissos = props.compromissoCodigos?.length ?? 0;
  return totalPastas > 1 || totalCompromissos > 1;
});

async function handleSubmit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  // Iniciar loading antes do upload
  setLoading();

  // Upload arquivos primeiro
  if (uploaderRef.value) {
    await uploaderRef.value.upload();
  }

  // Mapear arquivos para o formato esperado pelo backend
  const arquivos = model.arquivos.map((arquivo: any) => ({
    uid: arquivo.uid,
    name: arquivo.name,
    size: arquivo.size,
    type: arquivo.type,
  }));

  const compromissoCodigos = props.compromissoCodigos?.length
    ? props.compromissoCodigos
    : props.compromisso
      ? [props.compromisso.codigo]
      : [];

  await submit({
    pastaCodigos: props.pastaCodigos ?? [],
    compromissoCodigos,
    responsavelCodigo: model.responsavel?.value,
    descricao: model.descricao,
    dataPrazo: model.dataPrazo,
    arquivos,
  });

  // Mostrar notificacao de erro se falhou
  if (status.value === 'error') {
    notification.error({
      message: errorMessage.value || 'Tivemos um problema inesperado',
      duration: 10,
    });
  }
}

// Cleanup polling ao desmontar
onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <a-form
    v-if="showForm"
    ref="formRef"
    :model="model"
    autocomplete="off"
    layout="vertical"
  >
    <a-alert
      v-if="(props.pastaCodigos && props.pastaCodigos.length > 1) || (props.compromissoCodigos && props.compromissoCodigos.length > 1)"
      type="warning"
      message="Você irá realizar uma alteração em lote"
      show-icon
      style="margin-bottom: 24px"
    />

    <a-collapse
      v-if="props.pastaCodigos?.length === 1"
      v-model:active-key="mostrarHistoricoSolicitacoes"
      style="margin-bottom: 24px"
    >
      <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
        <CompromissoGqlDatatable
          :columns="colunas"
          :show-dates-filter="false"
          :show-reload-button="false"
          :show-status-search-bar="false"
          :show-filter="false"
          :variables="{
            order: [{ dataInicio: 'DESC' }],
            where: {
              tipo: {
                eq: 150,
              },
              fluxo: {
                pastaCodigo: {
                  in: props.pastaCodigos,
                },
              },
            },
          }"
        />
      </a-collapse-panel>
    </a-collapse>

    <a-form-item
      label="Responsável"
      name="responsavel"
      :rules="[
        {
          message: 'Responsável é obrigatório',
          required: true,
          trigger: 'change',
          type: 'object',
        },
      ]"
    >
      <GqlSelect
        v-model:value="model.responsavel"
        :query="usuariosQuery"
        :variables="{
          where: {
            ativo: {
              eq: true,
            },
          },
          order: [
            {
              nome: 'ASC',
            },
          ],
        }"
        placeholder="Selecione responsável"
        :allow-clear="true"
      />
    </a-form-item>

    <a-form-item
      label="Descrição"
      name="descricao"
      :rules="[
        {
          message: 'Descrição é obrigatória',
          required: true,
        },
      ]"
      style="padding-bottom: 0"
    >
      <a-textarea v-model:value="model.descricao" :rows="4" />
    </a-form-item>

    <a-form-item
      label="Prazo"
      name="dataPrazo"
      :rules="[{ message: 'Prazo é obrigatório', required: true }]"
    >
      <a-date-picker
        v-model:value="model.dataPrazo"
        format="DD/MM/YYYY HH:mm"
        value-format="DD/MM/YYYY HH:mm"
        placeholder="Selecione uma data"
        style="width: 100%"
        :disabled-date="disabledDate"
        :show-time="{ defaultValue: dayjs('17:00:00', 'HH:mm:ss') }"
      />
    </a-form-item>

    <Uploader
      ref="uploaderRef"
      v-model:file-list="model.arquivos"
      style="margin-top: 24px; margin-bottom: 24px"
    >
      <template #description>
        <p class="ant-upload-drag-icon">
          <CloudUploadOutlined />
        </p>
        <p class="ant-upload-text">Clique aqui ou arraste os arquivos</p>
        <p class="ant-upload-hint">Envie os arquivos necessários</p>
      </template>
    </Uploader>

    <a-form-item>
      <a-button
        type="primary"
        :loading="isLoading"
        size="large"
        :disabled="buttonsDisabled"
        @click.prevent="handleSubmit"
      >
        <template #icon>
          <SaveOutlined />
        </template>
        {{ isLoading ? 'Processando...' : 'Salvar' }}
      </a-button>
    </a-form-item>
  </a-form>

  <!-- Resultado de sucesso (simples) -->
  <a-result
    v-if="isSuccess && !isLote"
    status="success"
    title="Registro realizado com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado de sucesso (lote) -->
  <a-result
    v-if="isSuccess && isLote"
    status="success"
    title="Registros realizados com sucesso"
    :sub-title="`${result?.totalSucesso || 0} compromisso(s) processado(s) com sucesso`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 300px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
            </div>
            <CheckCircleOutlined style="color: #52c41a; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado parcial (lote com falhas) -->
  <a-result
    v-if="isPartial"
    status="warning"
    title="Processamento parcial"
    :sub-title="`${result?.totalSucesso || 0} sucesso(s), ${result?.totalFalha || 0} falha(s)`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 450px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left; flex: 1;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
              <span v-if="!item.sucesso" style="color: #ff4d4f; margin-left: 8px; font-size: 12px;">
                - {{ item.erro }}
              </span>
            </div>
            <CheckCircleOutlined v-if="item.sucesso" style="color: #52c41a; margin-left: 8px;" />
            <ExclamationCircleOutlined v-else style="color: #ff4d4f; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>
</template>
